import { createContext, forwardRef, useContext, type ComponentPropsWithoutRef, type Ref } from 'react';
import styled from 'styled-components';

import { MAIN_CONTENT_ID } from '@/components/accessibility/SkipToMain';

const InsideMainContentContext = createContext(false);

export const MainContentProvider = InsideMainContentContext.Provider;

export const useIsInsideMainContent = () => useContext(InsideMainContentContext);

const Main = styled.main``;
const NestedMain = styled.div``;

type MainContentProps = Omit<ComponentPropsWithoutRef<'main'>, 'id'>;

/**
 * The page's main landmark and the SkipToMain link's target. Extend it with
 * `styled(MainContent)` to add layout. Inside another main landmark (a MainContent, or a
 * GridWrapper carrying MAIN_CONTENT_ID) it renders a plain div, so nesting never produces two.
 */
const MainContent = forwardRef<HTMLElement, MainContentProps>(({ children, ...props }, ref) => {
	const isInsideMainContent = useIsInsideMainContent();

	if (isInsideMainContent) {
		return (
			<NestedMain ref={ref as Ref<HTMLDivElement>} {...props}>
				{children}
			</NestedMain>
		);
	}

	return (
		<MainContentProvider value>
			<Main ref={ref} {...props} id={MAIN_CONTENT_ID}>
				{children}
			</Main>
		</MainContentProvider>
	);
});

MainContent.displayName = 'MainContent';

export default MainContent;
